import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './styles/SortableList.scss';

import { useDragLayer, XYCoord } from 'react-dnd';
import { Box, Stack, Tooltip } from '@mui/material';
import { ArrowDownward, ArrowUpward, DragIndicator, SwapVert } from '@mui/icons-material';

import { classNames, componentClassName, isEnumValue } from '@dw/utils';

import { HStack } from '../HStack';
import { IconButton } from '../IconButton';
import { Draggable } from './Draggable';
import { DragDropZone } from './DragDropZone';
import { DragItemType } from './types';

export interface ISortableListItem {
  id: string;
}

enum SORTABLE_MOVE_DIRECTION {
  UP = 'UP',
  DOWN = 'DOWN'
}

interface ISortableDragData {
  id: string;
}

/**
 * Moves the item at `fromIndex` to `toIndex`, splice-style. The ONE reorder implementation shared
 * by every path in this component — Move Up/Down, live drag-hover, and (via handleDrop reading
 * whatever hover already produced) a drop — so they can never drift into subtly different reorder
 * semantics.
 */
function reorderBySplice<T>(list: T[], fromIndex: number, toIndex: number): T[] {
  const reordered = [...list];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);
  return reordered;
}

export interface ISortableListProps<T extends ISortableListItem> {
  items: T[];
  /**
   * A drag type unique to THIS list on the page. react-dnd only offers a drop when the drag type
   * and the zone's accept type match, so without a distinct value a card from an unrelated list
   * would be a legal drop target (every draggable in the app otherwise shares DragItemTypes.BOX).
   */
  dragType: DragItemType;
  /**
   * Renders one row's content.
   *
   * This is called from INSIDE `Draggable`'s drag-handle render prop, which `Draggable` invokes
   * TWICE per render — once to build a memoised (inert) drag preview, once for the live row. It
   * must therefore be a PURE function of `item`/`index` and must NOT call hooks: a hook here isn't
   * caught by lint, and it doesn't fail cleanly either. `Draggable`'s preview `useMemo` only
   * recomputes once its ref is attached, so a hook inside `renderItem` registers once on the first
   * render and twice on every render after — a conditional hook count that crashes React with
   * "Rendered more hooks than during the previous render" at mount, not a lint error.
   */
  renderItem: (item: T, index: number) => ReactNode;
  /** Fires with the COMPLETE new id order. The caller decides whether to stage it or persist it. */
  onOrderChanged: (orderedIds: string[]) => void;
  /**
   * Extra per-row affordances, rendered to the left of the move menu.
   *
   * Same purity contract as `renderItem` above — invoked twice per render (memoised preview + live
   * row) from inside `Draggable`'s render prop, so it must be pure and must NOT call hooks. Calling
   * a hook here crashes React at mount ("Rendered more hooks than during the previous render"),
   * not a lint warning.
   */
  rowActions?: (item: T, index: number) => ReactNode;
  /**
   * Opt-in: makes the whole row open something on click (e.g. ExamClassesList opens the class),
   * with a pointer cursor and a hover tint so it reads as clickable. The drag handle and the
   * trailing rowActions/move-menu column both stop click propagation (see the row markup below),
   * so neither dragging nor a row action can also fire this. Omitted, a row behaves exactly as
   * before - no click handler, no cursor/hover change.
   */
  onRowClick?: (item: T, index: number) => void;
  disabled?: boolean;
  /**
   * Tooltip shown on the handle and on the move menu while `disabled` — a control that is greyed
   * out with no explanation is a dead end.
   *
   * The move menu's copy only actually appears because Task 2 fixed the core `IconButton`: MUI puts
   * `pointer-events: none` on `.Mui-disabled`, which used to swallow the tooltip on exactly the
   * buttons that most needed one.
   */
  disabledReason?: string;
  className?: string;
}

/**
 * A vertically reorderable list.
 *
 * Drag-and-drop is the fast path; the per-row Move Up / Move Down menu is the touch and keyboard
 * path — the HTML5 backend emits no touch events and the Draggable is not focusable, so without
 * the menu this control would be mouse-only.
 *
 * The caller must render exactly ONE `DragAndDropContextProvider` somewhere above this component,
 * shared by every SortableList on the page. Nested providers shadow each other's drop-zone
 * registry.
 */
export function SortableList<T extends ISortableListItem>(props: ISortableListProps<T>) {

  const {
    items,
    dragType,
    renderItem,
    onOrderChanged,
    rowActions,
    onRowClick,
    disabled,
    disabledReason,
    className
  } = props;

  const cn = componentClassName('sortable-list');

  /**
   * The order actually rendered. Mirrors `items` whenever no drag of THIS list is in progress;
   * while dragging, `handleHover` below reshuffles it live so the list always shows what dropping
   * right now would produce, replacing the old border-only drop indicator. Transient by design —
   * see the two effects below for how it resyncs from `items` and how a cancelled drag reverts.
   */
  const [localOrder, setLocalOrder] = useState<T[]>(items);
  /** Mirrors `localOrder` for handleDrop to read without needing it in a dep array (see below). */
  const localOrderRef = useRef(localOrder);
  localOrderRef.current = localOrder;

  const wasDraggingRef = useRef(false);
  /** Set the instant a drop lands in THIS list (see handleDrop), so the drag-end effect below can tell a committed drop apart from a cancelled one. */
  const committedRef = useRef(false);

  const { isDraggingThisList } = useDragLayer((monitor) => ({
    // Scoped to this list's own dragType: the DragAndDropContextProvider a page renders is shared
    // by every SortableList on it (e.g. one per exam venue), and useDragLayer watches that whole
    // shared backend - without the type check, dragging in a SIBLING list would be seen here too.
    isDraggingThisList: monitor.isDragging() && monitor.getItemType() === dragType
  }));

  // Resyncs from the server/staged order whenever `items` changes. Deliberately NOT keyed on
  // isDraggingThisList (read, not depended on): a mid-drag prop change (an unrelated background
  // refetch elsewhere on the page) must not yank the list out from under the user's cursor, but
  // once a commit's own echo eventually arrives as a new `items` this still needs to catch up to
  // it. The drag-just-ended transition itself is the other effect's job, not this one's.
  useEffect(() => {
    if (!isDraggingThisList) {
      setLocalOrder(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Handles a drag ending. A COMMIT (handleDrop already ran, committedRef is true) needs no
  // correction here - localOrder already IS the order just sent to onOrderChanged, and the effect
  // above will settle it once the resulting items echo arrives, with no visible change since the
  // content already matches. A CANCEL (dropped outside every zone, or Escape - both leave
  // committedRef false, since handleDrop/onDropItem never ran) reverts the hover-shuffled preview
  // back to `items`, which is exactly the pre-drag order localOrder mirrored right up until the
  // drag started.
  useEffect(() => {
    if (isDraggingThisList) {
      wasDraggingRef.current = true;
      return;
    }

    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      if (!committedRef.current) {
        setLocalOrder(items);
      }
      committedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraggingThisList]);

  const moveItem = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex || toIndex >= localOrder.length) {
      return;
    }

    const reordered = reorderBySplice(localOrder, fromIndex, toIndex);
    setLocalOrder(reordered);
    onOrderChanged(reordered.map(x => x.id));
  }, [localOrder, onOrderChanged]);

  /**
   * Live drag-hover reorder: called as the dragged row passes over row `hoverIndex`. Everything is
   * derived from the updater's `current` (never the render-closure `localOrder`) so a burst of
   * hover events fired before a re-render flushes can never compute against a stale snapshot.
   * Idempotent by construction: it bails (returning the SAME array reference, which is also what
   * lets React skip the re-render) the instant the dragged item already sits at hoverIndex, and
   * uses the standard react-dnd midpoint-crossing check so a row doesn't flicker back and forth
   * when the cursor sits right on its boundary - it only moves once the cursor has actually
   * crossed past the middle of the row in the direction of travel.
   */
  const handleHover = useCallback((draggedId: string, hoverIndex: number, clientOffset: XYCoord | null, hoverBoundingRect: DOMRect | null) => {
    if (!clientOffset || !hoverBoundingRect) {
      return;
    }

    setLocalOrder(current => {
      const dragIndex = current.findIndex(x => x.id === draggedId);
      if (dragIndex < 0 || dragIndex === hoverIndex) {
        return current;
      }

      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return current;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return current;

      return reorderBySplice(current, dragIndex, hoverIndex);
    });
  }, []);

  /**
   * Commits the drag. `localOrder` already reflects the final position - every hover event kept it
   * live as the row travelled - so this only has to send it out. Reads the latest order via the ref
   * (not a `localOrder` dep) so this stays a stable function shared by every zone below.
   *
   * Wired to TWO layers of `DragDropZone` (see the render below): each row's own zone, AND one
   * wrapping the whole list. A per-row zone only covers that row's own rectangle - it has no idea
   * about the `rowGap` seam either side of it, or the sliver below the last row, so a release in
   * either of those (very easy to land in when the natural end point of "drag to the bottom" is
   * right where the last seam sits) used to hit no zone at all. `committedRef` stayed false, and
   * the drag-end effect above - unable to tell that apart from a genuine Escape/outside-drop
   * cancel - reverted the exact reorder the live preview had just shown. The list-wide zone closes
   * every one of those gaps: it accepts a drop ANYWHERE inside the list's own bounds, including the
   * seams, while a release genuinely outside the list still hits no zone and still reverts.
   *
   * That list-wide zone is a DOM ancestor of every row's zone, and react-dnd calls `drop()` on every
   * target the native `drop` event bubbles through (confirmed against HTML5BackendImpl - it collects
   * ALL target ids under the pointer via `dragenter`/`dragover`/`drop` listeners on each target node,
   * not just the innermost one) - so a drop directly on a row fires this via BOTH zones. The
   * `committedRef` guard below is what keeps `onOrderChanged` firing exactly once per drag either
   * way, rather than relying on either zone individually.
   */
  const handleDrop = useCallback(() => {
    if (committedRef.current) {
      return;
    }
    committedRef.current = true;
    onOrderChanged(localOrderRef.current.map(x => x.id));
  }, [onOrderChanged]);

  const listRef = useRef<HTMLDivElement>(null);
  /** What was measured for each row the LAST time the effect below ran - see its own comment. */
  const previousRowRectsRef = useRef<Map<string, DOMRect>>(new Map());

  /**
   * FLIP-slides every row to its new position whenever `localOrder`'s sequence changes - a live
   * drag-hover reshuffle, a Move Up/Down click, or the cancel-revert effect further up snapping
   * back to `items`. Deliberately generic over WHY the order changed rather than special-cased to
   * the drag path: whichever row(s) moved get the same glide, so the keyboard/touch Move menu and
   * an Escape-cancelled drag both read as smoothly as a live drag, for free.
   *
   * Classic FLIP, with direct DOM writes instead of React state so a fast multi-row reshuffle never
   * re-renders per animation frame (the one thing this must never become - lists here run to ~40
   * rows). `previousRowRectsRef` and this effect's own fresh `getBoundingClientRect()` calls are two
   * consecutive First/Last measurements: what a row's box was BEFORE this commit, and what it is
   * AFTER. Invert jumps a moved row back to its old spot with the transition switched off; Play
   * switches the transition back on and clears the offset, so the browser animates old -> new on its
   * own compositor thread. The transition's actual duration/easing lives in SortableList.scss on
   * `.dw-sortable-list--row` (shared with the pickup/drop opacity-shadow-border treatment, so the
   * two read as one interaction) - this effect only ever toggles that transition on/off and sets the
   * transform.
   *
   * `prefers-reduced-motion` is read fresh on every run (not cached) rather than via a listener -
   * cheap enough to check per reorder, and it means a mid-session OS setting change takes effect on
   * the very next reorder with no extra plumbing.
   */
  useLayoutEffect(() => {
    const previousRects = previousRowRectsRef.current;
    const rowElements = listRef.current?.querySelectorAll<HTMLElement>('[data-row-id]') ?? [];
    const nextRects = new Map<string, DOMRect>();
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    rowElements.forEach(el => {
      const id = el.dataset.rowId;
      if (!id) {
        return;
      }

      const newRect = el.getBoundingClientRect();
      nextRects.set(id, newRect);

      const oldRect = previousRects.get(id);
      // No previous measurement (a row that just mounted) has nowhere to slide FROM, and reduced
      // motion means every row skips straight to its resting transform of none.
      if (reduceMotion || !oldRect) {
        return;
      }

      const deltaY = oldRect.top - newRect.top;
      if (Math.abs(deltaY) < 1) {
        return;
      }

      // Invert: jump to the old position with no transition, so nothing paints mid-jump.
      el.style.transition = 'none';
      el.style.transform = `translateY(${deltaY}px)`;
      // Forces the browser to commit the jump above before Play re-enables the transition - without
      // this the two style writes would batch together and there would be nothing to animate FROM.
      el.getBoundingClientRect();

      // Play: release into the real position on the next frame, transition back on.
      requestAnimationFrame(() => {
        el.style.transition = '';
        el.style.transform = '';
      });
    });

    previousRowRectsRef.current = nextRects;
  }, [localOrder]);

  return (
    // Wraps the whole list, not just its rows - see handleDrop's comment above for why the commit
    // surface needs to be the full list rather than each row individually. `cn('surface')` (rather
    // than relying on descendant scoping) is what lets the SCSS below suppress the drop-target
    // outline on THIS zone specifically, the same way it already does for each row's - see
    // SortableList.scss.
    <DragDropZone<ISortableDragData>
      dragType={dragType}
      disabled={disabled}
      noVerticalTicks
      noHorizontalTicks
      onDropItem={handleDrop}
      className={cn('surface')}
    >
      <Stack ref={listRef} rowGap={1} className={classNames(cn(), className)}>
        {localOrder.map((item, index) => (
          <DragDropZone<ISortableDragData>
            key={item.id}
            dragType={dragType}
            disabled={disabled}
            noVerticalTicks
            noHorizontalTicks
            onHoverItem={(event) => handleHover(event.itemData.id, index, event.clientOffset, event.hoverBoundingRect)}
            onDropItem={handleDrop}
          >
            <Draggable<ISortableDragData>
              dragData={{ id: item.id }}
              dragType={dragType}
              disabled={disabled}
            >
              {(connectDragHandle) => (
                <HStack
                  data-row-id={item.id}
                  breakpoint={false}
                  columnGap={1}
                  className={classNames(cn('row'), { [cn('row', 'clickable')]: !!onRowClick })}
                  onClick={onRowClick ? () => onRowClick(item, index) : undefined}
                >
                  {/*
                    An MUI Tooltip, not a native `title`: every other affordance in the row uses one,
                    and a native title would be the odd one out - different chrome, a second's delay.
                    An empty title renders no tooltip at all, which is the right fallback when the
                    caller disabled the list without supplying a reason.

                    The Tooltip wraps the ICON, not the span: the span already carries the drag ref, and
                    handing Tooltip a child that owns a ref means relying on it to fork the two. The
                    icon is the whole visible handle anyway, and Tooltip-around-an-MUI-icon is the shape
                    core/InfoTooltip.tsx already uses.

                    `aria-hidden`, not `aria-label`: a bare, non-interactive `span` is not a valid host
                    for `aria-label` per the ARIA spec, so most screen readers drop it - the handle would
                    read as an unlabelled icon rather than announce anything useful. It's also not a
                    functional loss to hide it: the HTML5 drag backend has no keyboard or touch path, so
                    a keyboard/AT user could never operate this handle anyway. The Move Up / Move Down
                    menu below is the supported keyboard and touch path and already carries its own
                    accessible name.
                  */}
                  {/* Stops a plain (non-drag) click on the handle from also firing onRowClick - a real drag never reaches here at all, since the browser only ever fires `click` when mousedown/mouseup land on the same element and no drag was started in between. */}
                  <span
                    ref={connectDragHandle}
                    className={classNames(cn('handle'), { [cn('handle', 'disabled')]: disabled })}
                    aria-hidden
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title={disabled ? (disabledReason ?? '') : 'Drag to reorder'}>
                      <DragIndicator fontSize='small' color='disabled' />
                    </Tooltip>
                  </span>

                  {/*
                    A Box, not a Stack: Stack forces a column flex context on its children, so a
                    consumer returning two sibling nodes from renderItem would get them stacked
                    vertically instead of flowing as written. Box just provides the flex:1 sizing.

                    `minWidth={0}` is what lets long row content ELLIPSISE instead of shoving the
                    actions column off screen. A flex item defaults to `min-width: auto`, which
                    refuses to shrink below its content's intrinsic width, so `flex={1}` alone
                    grows this Box to fit the longest word/line the consumer renders and pushes
                    rowActions and the move menu past the right edge - `noWrap` on the text inside
                    cannot save it, because the overflow happens a level up from the text. Every
                    SortableList row with variable-length content has this; a notice with a long
                    body is just where it showed first.
                  */}
                  <Box flex={1} minWidth={0}>
                    {renderItem(item, index)}
                  </Box>

                  {/* One stopPropagation covers every action in this column - the built-in Move menu below AND whatever the caller renders via rowActions (e.g. Edit/Delete) - so none of them also fires onRowClick. */}
                  <HStack breakpoint={false} columnGap={0.5} onClick={(e) => e.stopPropagation()}>
                    {rowActions ? rowActions(item, index) : null}

                    {localOrder.length > 1 && (
                      <IconButton
                        icon={<SwapVert />}
                        size='small'
                        tooltip={disabled ? disabledReason ?? 'Move' : 'Move'}
                        disabled={disabled}
                        menuOptions={[
                          { text: 'Move Up', icon: <ArrowUpward />, value: SORTABLE_MOVE_DIRECTION.UP, hidden: index === 0 },
                          { text: 'Move Down', icon: <ArrowDownward />, value: SORTABLE_MOVE_DIRECTION.DOWN, hidden: index === localOrder.length - 1 }
                        ]}
                        onMenuOptionSelected={(option) => {
                          if (isEnumValue(SORTABLE_MOVE_DIRECTION, option?.value)) {
                            moveItem(index, index + (option.value === SORTABLE_MOVE_DIRECTION.UP ? -1 : 1));
                          }
                        }}
                      />
                    )}
                  </HStack>
                </HStack>
              )}
            </Draggable>
          </DragDropZone>
        ))}
      </Stack>
    </DragDropZone>
  );
}
